/**
 * Writes a theme onto an element: `<html>` for a whole page, or any box for a scoped preview
 * (theme.css derives every role inside `@theme inline`, so a themed box follows its own theme).
 */
import type { StorefrontTheme } from "@loot-os/api-contract";

import { loadFonts } from "./fonts.js";
import { resolveThemeVars, type ThemeVars } from "./themeVars.js";

/** Marks an element a theme was applied to; theme.css paints its canvas and text. */
export const THEME_ROOT_ATTRIBUTE = "data-theme-root";

/** Applies `theme` to `element`, replacing whatever theme it had. Returns what it wrote. */
export function applyTheme(element: HTMLElement, theme?: StorefrontTheme | null): ThemeVars {
  const vars = resolveThemeVars(theme);
  const { style } = element;
  // The set is always complete, so writing it replaces a previous theme without clearing first.
  for (const [name, value] of Object.entries(vars.properties)) {
    style.setProperty(name, value);
  }
  style.setProperty("font-family", vars.fontFamily);
  if (vars.colorScheme) style.setProperty("color-scheme", vars.colorScheme);
  else style.removeProperty("color-scheme");
  element.setAttribute(THEME_ROOT_ATTRIBUTE, "");
  void loadFonts(vars.fonts);
  return vars;
}
